import { Procurement } from '@/types/database.types';
import { ProcurementItem } from '@/types/database/procurement-items';

/**
 * Calculate the total amount for a procurement including all costs
 * (product cost + transport + loading + agent + other costs)
 */
export const calculateTotalAmount = (procurement: Procurement): number => {
  // Calculate product cost from items if available
  if (procurement.items && procurement.items.length > 0) {
    const productCost = procurement.items.reduce((total, item) => {
      return total + (item.price_per_unit * item.quantity);
    }, 0);
    
    // Get the total quantity to calculate additional costs
    const totalQuantity = procurement.items.reduce((total, item) => total + item.quantity, 0);
    
    // Calculate additional costs from the costs table
    const transportCost = (procurement.costs?.transport_cost_per_unit || 0) * totalQuantity;
    const loadingCost = (procurement.costs?.loading_unloading_cost_per_unit || 0) * totalQuantity;
    const agentCost = (procurement.costs?.agent_cost_per_unit || 0) * totalQuantity;
    const otherCost = (procurement.costs?.other_cost_per_unit || 0) * totalQuantity;
    
    return productCost + transportCost + loadingCost + agentCost + otherCost;
  }
  
  // Fallback for legacy data or when costs are stored in the costs table
  if (procurement.costs && procurement.total_quantity) {
    const quantity = procurement.total_quantity;
    const productCost = (procurement.costs.price_per_unit || 0) * quantity;
    const transportCost = (procurement.costs.transport_cost_per_unit || 0) * quantity;
    const loadingCost = (procurement.costs.loading_unloading_cost_per_unit || 0) * quantity;
    const agentCost = (procurement.costs.agent_cost_per_unit || 0) * quantity;
    const otherCost = (procurement.costs.other_cost_per_unit || 0) * quantity;
    
    return productCost + transportCost + loadingCost + agentCost + otherCost;
  }
  
  // Final fallback: use total_amount if available
  return procurement.total_amount || 0;
};

/**
 * Calculate the average price per unit (including all costs)
 */
export const calculateAveragePricePerUnit = (procurement: Procurement): number => {
  const totalQuantity = calculateTotalQuantity(procurement);
  if (totalQuantity === 0) return 0;
  
  const totalAmount = calculateTotalAmount(procurement);
  return totalAmount / totalQuantity;
};

/**
 * Calculate net payable quantity in KG for a single item
 * Formula: Warehouse Received Quantity - Quality Compensation
 * Returns 0 if item is rejected
 */
export const calculateItemNetPayableKg = (item: ProcurementItem): number => {
  // If item is rejected, net payable is 0
  if (item.item_quality?.is_rejected) {
    return 0;
  }
  
  // Use warehouse received quantity if available, otherwise fall back to original quantity
  const receivedQty = item.item_warehouse?.received_quantity_kg ?? item.quantity;
  
  // Get compensation from item quality data
  const compensation = item.item_quality?.compensation_kg 
    ?? item.item_quality?.preliminary_compensation_kg 
    ?? 0;
  
  return Math.max(0, receivedQty - compensation);
};

/**
 * Calculate net payable amount for a single item
 * Formula: Net Payable KG × Item Price
 */
export const calculateItemNetPayableAmount = (item: ProcurementItem): number => {
  const netPayableKg = calculateItemNetPayableKg(item);
  const pricePerUnit = item.price_per_unit || 0;
  
  return netPayableKg * pricePerUnit;
};

/**
 * Calculate net payable quantity in KG
 * Uses item-level data when available for accuracy
 * Formula: Sum of (Warehouse Received Quantity - Compensation) per item
 */
export const calculateNetPayableKg = (procurement: Procurement): number => {
  // If we have items with item-level warehouse or quality data, use item-level calculation
  if (procurement.items && procurement.items.length > 0) {
    // Only use item-level calculation when item_warehouse data exists
    // If only item_quality exists (no item_warehouse), fall through to aggregate path
    // to avoid using ordered quantity as fallback instead of actual warehouse received quantity
    const hasItemWarehouseData = procurement.items.some(
      item => item.item_warehouse
    );
    
    if (hasItemWarehouseData) {
      return procurement.items.reduce((total, item) => {
        return total + calculateItemNetPayableKg(item);
      }, 0);
    }
  }
  
  // Fallback for legacy data: use aggregate-level calculation
  const receivedQty = (procurement.warehouse as any)?.received_quantity_kg 
    ?? procurement.warehouse?.net_kg_payable
    ?? calculateTotalQuantity(procurement);
  
  const compensation = (procurement.quality as any)?.compensation_kg 
    ?? (procurement.quality as any)?.preliminary_compensation_kg 
    ?? 0;
  
  // For legacy data where warehouse.net_kg_payable was manually set
  if ((procurement.warehouse as any)?.received_quantity_kg !== undefined) {
    return Math.max(0, receivedQty - compensation);
  }
  
  if (procurement.warehouse?.net_kg_payable !== undefined) {
    return Math.max(0, procurement.warehouse.net_kg_payable);
  }
  
  return Math.max(0, calculateTotalQuantity(procurement) - compensation);
};

/**
 * Calculate net payable amount in currency
 * Uses item-level data when available for accurate per-item pricing
 * Formula: Sum of (Item Net Payable KG × Item Price) per item
 */
export const calculateNetPayableAmount = (procurement: Procurement): number => {
  // If we have items with item-level data, calculate per-item and sum
  if (procurement.items && procurement.items.length > 0) {
    // Only use item-level calculation when item_warehouse data exists
    const hasItemWarehouseData = procurement.items.some(
      item => item.item_warehouse
    );
    
    if (hasItemWarehouseData) {
      const productTotal = procurement.items.reduce((total, item) => {
        return total + calculateItemNetPayableAmount(item);
      }, 0);
      
      // Add additional per-unit costs based on net payable quantity
      const netPayableKg = calculateNetPayableKg(procurement);
      const transportCost = (procurement.costs?.transport_cost_per_unit || 0) * netPayableKg;
      const loadingCost = (procurement.costs?.loading_unloading_cost_per_unit || 0) * netPayableKg;
      const agentCost = (procurement.costs?.agent_cost_per_unit || 0) * netPayableKg;
      const otherCost = (procurement.costs?.other_cost_per_unit || 0) * netPayableKg;
      
      return productTotal + transportCost + loadingCost + agentCost + otherCost;
    }
  }
  
  // Fallback: use average price per unit (legacy behavior)
  const netPayableKg = calculateNetPayableKg(procurement);
  const pricePerUnit = calculateAveragePricePerUnit(procurement);
  
  return netPayableKg * pricePerUnit;
};

export const calculateTotalQuantity = (procurement: Procurement): number => {
  if (procurement.items && procurement.items.length > 0) {
    return procurement.items.reduce((total, item) => total + item.quantity, 0);
  }
  
  return procurement.total_quantity || 0;
};

/**
 * Get quality compensation in KG (aggregate level)
 */
export const getQualityCompensationKg = (procurement: Procurement): number => {
  // First check if we have item-level data
  if (procurement.items && procurement.items.length > 0) {
    const hasItemLevelQuality = procurement.items.some(item => item.item_quality);
    
    if (hasItemLevelQuality) {
      return procurement.items.reduce((total, item) => {
        const compensation = item.item_quality?.compensation_kg 
          ?? item.item_quality?.preliminary_compensation_kg 
          ?? 0;
        return total + compensation;
      }, 0);
    }
  }
  
  // Fallback to aggregate quality
  return (procurement.quality as any)?.compensation_kg 
    ?? (procurement.quality as any)?.preliminary_compensation_kg 
    ?? 0;
};

/**
 * Get warehouse received quantity in KG (aggregate level)
 */
export const getWarehouseReceivedKg = (procurement: Procurement): number => {
  // First check if we have item-level warehouse data
  if (procurement.items && procurement.items.length > 0) {
    const hasItemLevelWarehouse = procurement.items.some(item => item.item_warehouse);
    
    if (hasItemLevelWarehouse) {
      return procurement.items.reduce((total, item) => {
        const received = item.item_warehouse?.received_quantity_kg ?? item.quantity;
        return total + received;
      }, 0);
    }
  }
  
  // Fallback to aggregate warehouse
  return (procurement.warehouse as any)?.received_quantity_kg 
    ?? calculateTotalQuantity(procurement);
};
